import { Icon } from '@iconify/react';
import React, { useEffect, useState, useCallback } from 'react';
import { changePassword, editProfile, getUserProfileData } from '../utils/api/userAPI';
import { convertToIndianTimeZone } from '../utils/utils';
import { Avatar } from './child/Avatar';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const ViewProfileLayer = () => {
  const [imagePreview, setImagePreview] = useState(
    'assets/images/user-grid/user-grid-img13.png'
  );
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [newPasswordVisible, setNewPasswordVisible] = useState(false);

  const [userData, setUserData] = useState(null);
  const [editFormData, setEditFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
  });

  // (Includes currentPassword, newPassword)
  const [changePasswordForm, setChangePassword] = useState({
    currentPassword: '',
    newPassword: '',
  });

  // Fetch user data on mount
  useEffect(() => {
    getUserProfileData()
      .then((data) => {
        setUserData(data);
      })
      .catch((err) => {
        console.error('Error fetching user data:', err);
      });
  }, []);

  // Populate editFormData once userData is loaded
  useEffect(() => {
    if (userData && userData.user) {
      setEditFormData({
        firstName: userData.user.firstName || '',
        lastName: userData.user.lastName || '',
        phone: userData.user.phone || '',
      });
    }
  }, [userData]);

  // Handle password change
  const handleUpdatePassword = () => {
    changePassword(changePasswordForm)
      .then((res) => {
        toast.success('Password changed!');
        // Clear out password fields
        setChangePassword({ currentPassword: '', newPassword: '' });
      })
      .catch((e) => {
        toast.error(e.message);
      });
  };

  // Handle form inputs for profile edit
  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  // Handle form inputs for password change
  const handlePasswordInputChange = (e) => {
    const { id, value } = e.target;
    setChangePassword((prev) => ({
      ...prev,
      [id]: value,
    }));
  };

  // Reset edit form back to original user data
  const handleCancel = () => {
    if (userData && userData.user) {
      setEditFormData({
        firstName: userData.user.firstName || '',
        lastName: userData.user.lastName || '',
        phone: userData.user.phone || '',
      });
    } else {
      setEditFormData({ firstName: '', lastName: '', phone: '' });
    }
  };

  // Save edited profile, then refresh userData
  const handleSave = useCallback(() => {
    editProfile(editFormData)
      .then(() => {
        // Return the promise so that the next `.then` gets the data
        return getUserProfileData();
      })
      .then((updatedData) => {
        setUserData(updatedData);
        toast.success('Profile saved!');
      })
      .catch((err) => {
        console.error('Error saving profile:', err);
        toast.error(err.message);
      });
  }, [editFormData]);

  // Toggle visibility for current password
  const togglePasswordVisibility = () => {
    setPasswordVisible((prev) => !prev);
  };

  // Toggle visibility for new password
  const toggleNewPasswordVisibility = () => {
    setNewPasswordVisible((prev) => !prev);
  };

  // (If you plan to let users upload a new avatar/image in the future)
  const readURL = (input) => {
    if (input.target.files && input.target.files[0]) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(input.target.files[0]);
    }
  };

  if (!userData || !userData.user) {
    return (
      <div className="container-fluid" style={{ padding: "24px" }}>
        <div className="row g-4">
          {/* LEFT CARD: Profile summary */}
          <div className="col-lg-4">
            <div className="card" style={{ borderRadius: "12px" }}>
              <div className="card-body d-flex flex-column align-items-center">
                {/* Avatar */}
                <div
                  className="shimmer"
                  style={{
                    width: "120px",
                    height: "120px",
                    borderRadius: "9999px",
                    marginBottom: "16px",
                  }}
                />

                {/* Name */}
                <div
                  className="shimmer"
                  style={{ width: "180px", height: "18px", borderRadius: "4px", marginBottom: "8px" }}
                />
                {/* Email */}
                <div
                  className="shimmer"
                  style={{ width: "220px", height: "14px", borderRadius: "4px", marginBottom: "16px" }}
                />

                <hr style={{ width: "100%", margin: "16px 0" }} />

                {/* Personal Info header */}
                <div
                  className="shimmer shimmer-badge"
                  style={{ width: "140px", height: "16px", marginBottom: "14px" }}
                />

                {/* Info rows */}
                {["Full Name", "Email", "Phone Number", "Organization Name", "Updated At"].map((label, i) => (
                  <div
                    key={label}
                    className="d-flex align-items-center justify-content-between"
                    style={{ width: "100%", marginBottom: i === 4 ? 0 : "12px" }}
                  >
                    <div className="shimmer shimmer-text" style={{ width: "130px" }} />
                    <div className="shimmer" style={{ width: "55%", height: "14px", borderRadius: "4px" }} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* RIGHT CARD: Edit form */}
          <div className="col-lg-8">
            <div className="card" style={{ borderRadius: "12px" }}>
              <div className="card-body">
                {/* Tabs header */}
                <div className="d-flex align-items-center" style={{ gap: "12px", marginBottom: "20px" }}>
                  <div className="shimmer shimmer-badge" style={{ width: "110px", height: "18px" }} />
                  <div className="shimmer shimmer-badge" style={{ width: "140px", height: "18px" }} />
                </div>

                {/* Form fields */}
                <div className="mb-3">
                  <div className="shimmer shimmer-text" style={{ width: "100px", marginBottom: "8px" }} />
                  <div className="shimmer" style={{ width: "100%", height: "44px", borderRadius: "8px" }} />
                </div>

                <div className="mb-3">
                  <div className="shimmer shimmer-text" style={{ width: "110px", marginBottom: "8px" }} />
                  <div className="shimmer" style={{ width: "100%", height: "44px", borderRadius: "8px" }} />
                </div>

                <div className="mb-4">
                  <div className="shimmer shimmer-text" style={{ width: "80px", marginBottom: "8px" }} />
                  <div className="shimmer" style={{ width: "100%", height: "44px", borderRadius: "8px" }} />
                </div>

                {/* Actions */}
                <div className="d-flex justify-content-center" style={{ gap: "16px" }}>
                  <div className="shimmer shimmer-actions" style={{ width: "120px", height: "40px", borderRadius: "8px" }} />
                  <div className="shimmer shimmer-actions" style={{ width: "120px", height: "40px", borderRadius: "8px" }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

    );
  }

  const { user } = userData;

  return (
    <motion.div initial={{
      opacity: 0
    }}
      animate={{
        opacity: 1
      }}
      transition={{
        duration: 1
      }}
      className="row gy-4">
      {/* ─── LEFT COLUMN: PROFILE CARD ─────────────────────────────────────── */}
      <div className="col-lg-4">
        <div className="user-grid-card position-relative border radius-16 overflow-hidden bg-base h-100">
          <div className="pb-24 ms-16 mb-24 me-16" style={{ marginTop: '20px' }}>
            <div className="text-center border-top-0 border-start-0 ">
              <Avatar fullName={user.displayName} />
            </div>
            <div className="text-center border border-top-0 border-start-0 border-end-0">
              <h6 className="mb-0 mt-16">{user.displayName}</h6>
              <span className="text-secondary-light mb-16">{user.email}</span>
            </div>

            <div className="mt-24">
              <h6 className="text-xl mb-16">Personal Info</h6>
              <ul>
                <li className="d-flex align-items-center gap-1 mb-12">
                  <span className="w-30 text-md fw-semibold text-primary-light">
                    Full Name
                  </span>
                  <span className="w-70 text-secondary-light fw-medium">
                    : {user.displayName}
                  </span>
                </li>

                <li className="d-flex align-items-center gap-1 mb-12">
                  <span className="w-30 text-md fw-semibold text-primary-light">
                    Email
                  </span>
                  <span className="w-70 text-secondary-light fw-medium">
                    : {user.email}
                  </span>
                </li>

                <li className="d-flex align-items-center gap-1 mb-12">
                  <span className="w-30 text-md fw-semibold text-primary-light">
                    Phone Number
                  </span>
                  <span className="w-70 text-secondary-light fw-medium">
                    : {user.phone}
                  </span>
                </li>

                <li className="d-flex align-items-center gap-1 mb-12">
                  <span className="w-30 text-md fw-semibold text-primary-light">
                    Organization Name
                  </span>
                  <span className="w-70 text-secondary-light fw-medium">
                    : {user.organizationName}
                  </span>
                </li>

                {user.preferredLanguage && user.preferredLanguage !== 'string' && (
                  <li className="d-flex align-items-center gap-1 mb-12">
                    <span className="w-30 text-md fw-semibold text-primary-light">
                      Languages
                    </span>
                    <span className="w-70 text-secondary-light fw-medium">
                      : {user.preferredLanguage}
                    </span>
                  </li>
                )}

                <li className="d-flex align-items-center gap-1">
                  <span className="w-30 text-md fw-semibold text-primary-light">
                    Updated At
                  </span>
                  <span className="w-70 text-secondary-light fw-medium">
                    : {convertToIndianTimeZone(user.updatedAt)}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* ─── RIGHT COLUMN: TABS FOR EDIT / CHANGE PASSWORD ──────────────────── */}
      <div className="col-lg-8 ">
        <div className="card h-100 ">
          <div className="card-body p-24">
            <ul
              className="nav border-gradient-tab nav-pills mb-20 d-inline-flex"
              id="pills-tab"
              role="tablist"
            >
              <li className="nav-item" role="presentation">
                <button
                  className="nav-link d-flex align-items-center px-24 active"
                  id="pills-edit-profile-tab"
                  data-bs-toggle="pill"
                  data-bs-target="#pills-edit-profile"
                  type="button"
                  role="tab"
                  aria-controls="pills-edit-profile"
                  aria-selected="true"
                >
                  Edit Profile
                </button>
              </li>

              <li className="nav-item" role="presentation">
                <button
                  className="nav-link d-flex align-items-center px-24"
                  id="pills-change-password-tab"
                  data-bs-toggle="pill"
                  data-bs-target="#pills-change-password"
                  type="button"
                  role="tab"
                  aria-controls="pills-change-password"
                  aria-selected="false"
                  tabIndex={-1}
                >
                  Change Password
                </button>
              </li>
            </ul>

            <div className="tab-content " id="pills-tabContent">
              {/* ─── EDIT PROFILE TAB ────────────────────────────────────────────── */}
              <div
                className="tab-pane fade show active "
                id="pills-edit-profile"
                role="tabpanel"
                aria-labelledby="pills-edit-profile-tab"
                tabIndex={0}
              >
                <form>
                  <div className="row">
                    <div className="">
                      <div className="mb-20">
                        <label
                          htmlFor="firstName"
                          className="form-label fw-semibold text-primary-light text-sm mb-8"
                        >
                          First Name <span className="text-danger-600">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control radius-8"
                          id="firstName"
                          placeholder="Enter First Name"
                          value={editFormData.firstName}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    <div className="">
                      <div className="mb-20">
                        <label
                          htmlFor="lastName"
                          className="form-label fw-semibold text-primary-light text-sm mb-8"
                        >
                          Last Name <span className="text-danger-600">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control radius-8"
                          id="lastName"
                          placeholder="Enter Last Name"
                          value={editFormData.lastName}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    <div className="">
                      <div className="mb-20">
                        <label
                          htmlFor="phone"
                          className="form-label fw-semibold text-primary-light text-sm mb-8"
                        >
                          Phone <span className="text-danger-600">*</span>
                        </label>
                        <input
                          type="tel"
                          className="form-control radius-8"
                          id="phone"
                          placeholder="Enter phone number"
                          value={editFormData.phone}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="d-flex align-items-center justify-content-center gap-3">
                    <button
                      type="button"
                      className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-56 py-11 radius-8"
                      onClick={handleCancel}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
                      onClick={handleSave}
                    >
                      Save
                    </button>
                  </div>
                </form>
              </div>

              {/* ─── CHANGE PASSWORD TAB ─────────────────────────────────────────── */}
              <div
                className="tab-pane fade"
                id="pills-change-password"
                role="tabpanel"
                aria-labelledby="pills-change-password-tab"
                tabIndex={0}
              >
                {/* ─ Current Password Field ─ */}
                <div className="mb-20">
                  <label
                    htmlFor="currentPassword"
                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                  >
                    Current Password <span className="text-danger-600">*</span>
                  </label>
                  <div className="position-relative">
                    <input
                      type={passwordVisible ? 'text' : 'password'}
                      className="form-control radius-8"
                      id="currentPassword"
                      placeholder="Enter Current Password"
                      value={changePasswordForm.currentPassword}
                      onChange={handlePasswordInputChange}
                    />
                    <span
                      className="cursor-pointer position-absolute end-0 top-50 translate-middle-y me-16 text-secondary-light"
                      onClick={togglePasswordVisibility}
                    >
                      <Icon
                        icon={passwordVisible ? 'ri:eye-off-line' : 'ri:eye-line'}
                        fontSize="1.2rem"
                      />
                    </span>
                  </div>
                </div>

                {/* ─ New Password Field ─ */}
                <div className="mb-20">
                  <label
                    htmlFor="newPassword"
                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                  >
                    New Password <span className="text-danger-600">*</span>
                  </label>
                  <div className="position-relative">
                    <input
                      type={newPasswordVisible ? 'text' : 'password'}
                      className="form-control radius-8"
                      id="newPassword"
                      placeholder="Enter New Password"
                      value={changePasswordForm.newPassword}
                      onChange={handlePasswordInputChange}
                    />
                    <span
                      className="cursor-pointer position-absolute end-0 top-50 translate-middle-y me-16 text-secondary-light"
                      onClick={toggleNewPasswordVisibility}
                    >
                      <Icon
                        icon={
                          newPasswordVisible ? 'ri:eye-off-line' : 'ri:eye-line'
                        }
                        fontSize="1.2rem"
                      />
                    </span>
                  </div>
                </div>

                {/* ─ Submit Change Password Button ─ */}
                <div className="d-flex align-items-center justify-content-center gap-3">
                  <button
                    type="button"
                    className="btn btn-primary border border-primary-600 text-md px-56 py-12 radius-8"
                    onClick={handleUpdatePassword}
                  >
                    Update Password
                  </button>
                </div>
              </div>
              {/* ─ End: Change Password Tab ──────────────────────────────────── */}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default ViewProfileLayer;
